/**
 * The design token contract (D24.2), as specified by the **Design System Brief**
 * (Confluence CAM › Design System Brief — Going Once).
 *
 * **The brief is the source of truth for these names.** Engineering owns the slots;
 * Ciera's team owns the values. The names here are copied from the brief rather than
 * invented, because a system that arrives speaking different words means the app gets
 * rewritten to match — which is the expensive path the brief and D24 both exist to avoid.
 *
 * **Values are portable, not CSS.** The brief asks for plain units — numbers and hex —
 * because mobile is React Native and `'16px'` is not a value RN can use. So lengths are
 * unitless numbers, line heights are multipliers, durations are milliseconds, shadows
 * are structured, and each renderer formats them (`toCss` here; the RN adapter when
 * Lane G's mobile half lands). One token layer, two renderers (D24.5).
 *
 * **Color is per-theme; everything else is not.** Dark is the default because the brief
 * says so: live-video apps are overwhelmingly dark, and dark chrome around bright video
 * is easier over a two-hour show. Spacing and type do not change with the theme, so
 * duplicating them per theme would create two places to edit one decision.
 *
 * ## The dark palette is PROVISIONAL, extracted from Ciera's mockup (2026-09-09)
 *
 * Values come from `Going Once App.dc.html` in the Claude Design project
 * `f92c7c8f-d379-411d-b7fd-9edc10e0f8b2` (12–16 phone frames, built 2026-08-25→27). That
 * file contains **no tokens** — every value is an inline hex — so the mapping from a hex
 * to a semantic slot is an inference, and the inferences are listed in
 * `MAPPING_TO_CONFIRM` below. Treat them as a proposal to Ciera's team, not as their
 * decision. The light palette is untouched placeholder: the brief says dark first, and
 * they have not designed light.
 *
 * The strong evidence, so the reasoning is checkable rather than trusted:
 * `timerColor: s.sec <= 5 ? '#FF3D71' : '#00D4B4'` and
 * `{ meta: 'Winning 2 · outbid on 1', metaC: '#FFC247' }` — teal is leading, amber is
 * outbid, pink is urgency.
 *
 * The rule that makes replacing any of it cheap: no literal hex, length or font name
 * appears in any component, enforced by `apps/web/src/ui/tokens-only.spec.ts`.
 */

/** Color slots, per theme. Names are the brief's. */
export interface Palette {
  'bg.base': string;
  'bg.raised': string;
  'bg.sunken': string;
  /**
   * The bottom bar and any pinned footer — `--bg-bar` in the design system.
   *
   * A hair lighter than `bg.base`, which is the whole point: the nav has to read as
   * chrome sitting on the page rather than as more page. Approximating it with `bg.base`
   * is what the first tab bar did, and the bar disappeared into the content above it.
   */
  'bg.bar': string;
  /**
   * Menus and the admin console's filter side panel — `--bg-sheet` in the design system.
   * A surface that sits over the page without a scrim, so in dark it is a step lighter
   * than `bg.bar` to read as on top; in light all three are white, and only its border and
   * shadow set it apart (`MAPPING_TO_CONFIRM`). In `ADDED_BEYOND_BRIEF`.
   */
  'bg.sheet': string;
  'fg.default': string;
  'fg.muted': string;
  /** Text on an accent or otherwise inverted surface. */
  'fg.inverse': string;
  'accent.default': string;
  'accent.hover': string;
  /**
   * **The second brand accent, and the seller's color.**
   *
   * The design system names it `accent2` and describes it as *"YOUR value: your max, your
   * proxy, a confirmation"* — and the navigation review makes it load-bearing a second
   * way: the bottom bar turns teal in seller mode and pink in buyer mode, which is how a
   * person knows which app they are in without reading a label.
   *
   * `bid.leading` holds the same value and is **not** a duplicate: that slot means "you
   * are winning". The design system has it as an alias (`--bid-leading: var(--accent2-default)`)
   * and ours will follow when the palette is reconciled; until then a component picks the
   * slot that says what it means.
   */
  'accent2.default': string;
  /** The teal button pressed — `--accent2-hover` in the design system. In `ADDED_BEYOND_BRIEF`. */
  'accent2.hover': string;
  /**
   * The seller's teal as a wash and as a hairline — `--accent2-tint` and
   * `--accent2-tint-border` in the design system: the Rapid Mode card on Add Products (D61).
   * Translucent, so they sit on whatever surface is below. In `ADDED_BEYOND_BRIEF`.
   */
  'accent2.tint': string;
  'accent2.tint.border': string;
  /**
   * A quiet fill and its hairline — `--neutral-tint` and `--neutral-tint-border`: an icon
   * tile with no color to say, an unselected chip. In `ADDED_BEYOND_BRIEF`.
   */
  'neutral.tint': string;
  'neutral.tint.border': string;
  /** A tint of the accent for backgrounds — badges, selected rows. */
  'accent.subtle': string;
  /**
   * ⚡ The third color, and neither an accent nor a state: the purple the mockup spends
   * on the "Timed auction · not live" badge, toggles and surface gradients. It says
   * *what kind of thing this is* rather than *what you should do* or *how it is going*,
   * which is why it cannot borrow `accent` (a primary action) or `success` (a state).
   * Named in `ADDED_BEYOND_BRIEF` — the brief has no slot for it.
   */
  meta: string;
  'meta.subtle': string;
  success: string;
  'success.subtle': string;
  warning: string;
  'warning.subtle': string;
  danger: string;
  'danger.subtle': string;
  'border.default': string;
  'border.strong': string;
  /** Focus rings. Never removed, only restyled — an accessibility floor. */
  'border.focus': string;
  /** ⚡ "We are live now". Load-bearing: it is the difference between a show and a replay. */
  live: string;
  /**
   * ⚡ You are winning / you were beaten. The single most important state change in the
   * product, and it must not be carried by color alone — a colourblind buyer has to
   * read it peripherally too, so components pair these with a non-color channel.
   */
  'bid.leading': string;
  'bid.outbid': string;
  /** ⚡ The wash laid over live video so text on top of it stays legible. */
  'overlay.scrim': string;
  /** ⚡ Foreground over the scrim. Contrast here cannot be assumed — the video moves. */
  'overlay.fg': string;
  /**
   * ⚡ The second rank over video: `CURRENT BID`, `YOUR MAX`, `TIME` — the labels that
   * name the numbers without competing with them.
   *
   * `fg.muted` cannot do this job. It is a gray tuned against `bg.base`, and over a
   * scrim on moving video it disappears — so a panel built from it either shouts
   * (everything at full `overlay.fg`) or vanishes. The brief has one overlay foreground
   * because it never specified a panel over video; the live bid panel is that panel.
   * In `ADDED_BEYOND_BRIEF`.
   */
  'overlay.fg.muted': string;
}

/**
 * Dark first, per the brief.
 *
 * Placeholder values chosen only to be legible and to satisfy WCAG AA — see
 * `contrast.spec.ts`, which fails the build if a pair drops below it. They are not a
 * visual direction and should not be read as one.
 */
export const themes: Record<'dark' | 'light', Palette> = {
  /** PROVISIONAL — extracted from the mockup. See `MAPPING_TO_CONFIRM`. */
  dark: {
    'bg.base': '#0b0f1a',
    'bg.raised': '#131c31',
    /** Chips and inset rows — the mockup's category pills sit on this. */
    'bg.sunken': '#18223a',
    'bg.bar': '#0d1322',
    'bg.sheet': '#111a2e',
    'fg.default': '#ffffff',
    /**
     * The mockup's muted ladder runs `rgba(255,255,255,.4)`→`.75`, and **its two most-used
     * rungs fail WCAG AA** on `bg.raised`: `.4` measures 3.77:1 and `.45` measures 4.41:1
     * across 150 occurrences. Set at `.6` (6.81:1), which is the lowest rung that clears
     * the bar. This is a change to their palette, not a reading of it — flagged.
     */
    'fg.muted': 'rgba(255, 255, 255, 0.6)',
    'fg.inverse': '#0b0f1a',
    /**
     * **Pink is the action color, and the first extraction had this backwards.**
     *
     * Reading frequency put teal here; reading what the colors are *used on* corrects
     * it. Every primary CTA in the design is pink — "Raise max", "View order", "Set max,
     * bid $115 now", the bid button — as is the current-bid amount and the LIVE badge.
     * Teal is the second accent: positive state, your max, "See all". Mapped the other
     * way, every primary button in the app renders teal where the design says pink.
     */
    'accent.default': '#ff3d71',
    'accent.hover': '#ff5c88',
    'accent2.default': '#00d4b4',
    'accent2.hover': '#29e3c6',
    'accent2.tint': 'rgba(0, 212, 180, 0.14)',
    'accent2.tint.border': 'rgba(0, 212, 180, 0.35)',
    'neutral.tint': 'rgba(255, 255, 255, 0.07)',
    'neutral.tint.border': 'rgba(255, 255, 255, 0.14)',
    'accent.subtle': '#3a1030',
    /** `#7b61ff` from the mockup; the tint is it at ~14% over `bg.base`. */
    meta: '#a897ff',
    'meta.subtle': '#221a4a',
    /** Teal: winning, your max, secondary actions. `accent2` in all but name. */
    success: '#00d4b4',
    'success.subtle': '#04231f',
    warning: '#ffc247',
    'warning.subtle': '#2b1c00',
    /**
     * Danger shares pink with the accent, which is a question rather than a decision —
     * the design never shows a destructive action, so it has not had to distinguish
     * them. Listed in `MAPPING_TO_CONFIRM`.
     */
    danger: '#ff3d71',
    'danger.subtle': '#3a1030',
    'border.default': 'rgba(255, 255, 255, 0.08)',
    'border.strong': 'rgba(255, 255, 255, 0.14)',
    'border.focus': '#00d4b4',
    /** ⚡ The pulsing live dot — `@keyframes gopulse` in the mockup. */
    live: '#ff3d71',
    /** ⚡ Evidenced: the timer is teal until five seconds remain, then pink. */
    'bid.leading': '#00d4b4',
    /** ⚡ Evidenced: `metaC: '#FFC247'` on "Winning 2 · outbid on 1". */
    'bid.outbid': '#ffc247',
    'overlay.scrim': 'rgba(6, 8, 15, 0.72)',
    'overlay.fg': '#ffffff',
    'overlay.fg.muted': 'rgba(255, 255, 255, 0.72)',
  },
  light: {
    'bg.base': '#ffffff',
    'bg.raised': '#f6f6f7',
    'bg.sunken': '#ececee',
    'bg.bar': '#ffffff',
    'bg.sheet': '#ffffff',
    'fg.default': '#1b1b1f',
    'fg.muted': '#5f5f6b',
    'fg.inverse': '#ffffff',
    'accent.default': '#1b1b1f',
    'accent.hover': '#000000',
    // The design system keeps the brand fills identical across themes — they are the
    // literal app swatches — so this does not darken the way the placeholder light
    // palette does elsewhere.
    'accent2.default': '#00d4b4',
    'accent2.hover': '#29e3c6',
    'accent2.tint': 'rgba(0, 212, 180, 0.12)',
    'accent2.tint.border': 'rgba(0, 168, 143, 0.45)',
    'neutral.tint': 'rgba(19, 20, 25, 0.05)',
    'neutral.tint.border': 'rgba(19, 20, 25, 0.12)',
    'accent.subtle': '#eeeef1',
    meta: '#5b3fd6',
    'meta.subtle': '#eeeafe',
    success: '#15803d',
    // A shade lighter than the first placeholder (#e8f6ec), which put `success` on it at
    // 4.496:1 — under AA for the Leading badge and the auction's leading banner alike.
    'success.subtle': '#ecf8ef',
    warning: '#a16207',
    'warning.subtle': '#fdf5e3',
    danger: '#b3261e',
    'danger.subtle': '#fdeceb',
    'border.default': '#e0e0e4',
    'border.strong': '#b8b8c0',
    'border.focus': '#1d4ed8',
    live: '#b3261e',
    'bid.leading': '#15803d',
    'bid.outbid': '#a16207',
    'overlay.scrim': 'rgba(0, 0, 0, 0.55)',
    'overlay.fg': '#ffffff',
    'overlay.fg.muted': 'rgba(255, 255, 255, 0.72)',
  },
};

/** One scale for padding, margin and gap. Unitless — the renderer adds the unit. */
/**
 * PROVISIONAL. The mockup's gaps are 1px-granular (4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
 * 18) and denser than the scale this contract shipped with. Pulled tighter to match, but
 * the underlying question is for design: **which of these are decisions and which are
 * drift?** A scale is what makes a layout consistent; fifteen values is not a scale.
 */
export const space = { xs: 4, sm: 8, md: 12, lg: 20, xl: 32, '2xl': 48 } as const;

/**
 * PROVISIONAL. The mockup uses **fifteen** radii — 2, 5, 6, 9, 10, 11, 12, 13, 14, 15,
 * 16, 18, 20, 22, 26 — hand-tuned per element rather than picked from a scale. These four
 * are the clusters those fall into. Collapsing them is a change to the design, and one
 * Ciera's team should confirm rather than discover.
 */
export const radius = { sm: 6, md: 13, lg: 18, full: 999 } as const;

/**
 * Whole type steps: size, line height and weight travel together or they drift apart.
 *
 * Line height is a **multiplier**, not a length — it is the one typographic value that
 * should scale with the size rather than be set against it, and it is portable to RN
 * without conversion.
 */
export interface TypeStep {
  size: number;
  line: number;
  weight: number;
  /**
   * The mockup sets its display steps in **italic 900** — an actual brand decision
   * (auctioneer's cadence), not a flourish, so it belongs in the step rather than in
   * whichever component remembers to add it.
   */
  italic?: boolean;
}

/** PROVISIONAL — the mockup's `font:` shorthands, which already express whole steps. */
export const type: Record<
  'hero' | 'display' | 'heading' | 'title' | 'stat' | 'body' | 'label' | 'caps' | 'mono' | 'countdown',
  TypeStep
> = {
  /**
   * ⚡ One step above display, for the two words a buyer sees once and remembers.
   *
   * The won artboard stacks `YOU` at the display step under `WON!` at this one, and the
   * difference between them *is* the celebration — set at one size the lockup reads as a
   * label. Nothing else on the platform should use it.
   */
  hero: { size: 52, line: 0.88, weight: 900, italic: true },
  /** `italic 900 34px/.9` in the mockup; the 46px variant is the logo lockup. */
  display: { size: 34, line: 0.9, weight: 900, italic: true },
  /** `italic 800 15px/1`. */
  heading: { size: 15, line: 1, weight: 800, italic: true },
  /**
   * An item's own name — upright, where `heading` is italic.
   *
   * **The two were one step until frame 1i was read.** `heading` comes from the brief and
   * the mockup's *section* labels, which are italic 900 uppercase; an item title on the
   * same frame is `800 17px/1.2` and upright. Setting a bag's name in the auctioneer's
   * italic makes the product read like a banner. In `ADDED_BEYOND_BRIEF`.
   */
  title: { size: 17, line: 1.2, weight: 800 },
  /**
   * ⚡ The number on a stat card — current bid, time left.
   *
   * `800 22px/1` in frame 1i and 24px on the older auction artboard, which is **between**
   * `heading` and `countdown` with nothing in the scale for it. Rendering these at the
   * countdown's 32px makes two cards that have to sit side by side on a 390pt frame
   * overflow their labels. In `ADDED_BEYOND_BRIEF`, with the 22-versus-24 disagreement
   * flagged in `MAPPING_TO_CONFIRM`.
   */
  stat: { size: 22, line: 1, weight: 800 },
  /** `500 15px/1.6`, with `600 14px/1.45` as the denser variant. */
  body: { size: 15, line: 1.6, weight: 500 },
  /** `500 11px/1.4` — labels, metadata, the 11px the mockup leans on heavily. */
  label: { size: 11, line: 1.4, weight: 500 },
  /**
   * `800 11px/1`, set in capitals — the design system's navigation labels (`navCaps`: Tabs and
   * SegmentedControl). The capitals and the design's 0.06em tracking are the component's: there is
   * no tracking slot. In `ADDED_BEYOND_BRIEF`.
   */
  caps: { size: 11, line: 1, weight: 800 },
  /** `800 10px/1 ui-monospace` — ids and anything read character by character. */
  mono: { size: 10, line: 1, weight: 800 },
  /**
   * ⚡ Its own step. **Unconfirmed** — the mockup renders the timer inline and does not
   * give it a distinct step, so this is a proposal: a clock people make money decisions
   * against, at a glance, over moving video, wants tabular figures and a weight that
   * survives a bright frame.
   */
  countdown: { size: 32, line: 1.1, weight: 800 },
};

/** Two families: the interface, and the one used where digits must not shift. */
export const family = {
  /** Archivo, from the mockup. Loaded in `apps/web/index.html`. */
  base: 'Archivo, system-ui, -apple-system, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/** Structured so both renderers can build a shadow from it. */
export interface Elevation {
  y: number;
  blur: number;
  spread: number;
  color: string;
  opacity: number;
}

export const elevation: Record<'raised' | 'overlay' | 'glow' | 'accentGlow', Elevation> = {
  raised: { y: 1, blur: 3, spread: 0, color: '#000000', opacity: 0.24 },
  /** The mockup's sheet: `0 30px 70px rgba(0,0,0,.6)`. */
  overlay: { y: 30, blur: 70, spread: 0, color: '#000000', opacity: 0.6 },
  /**
   * The colored glow under the primary CTA — `0 10px 26px rgba(0,212,180,.26)`.
   *
   * A slot beyond the brief, added because without it the bid button is visibly not the
   * design: the glow is how the CTA carries weight on a dark surface. Flagged in
   * `ADDED_BEYOND_BRIEF`.
   */
  glow: { y: 10, blur: 26, spread: 0, color: '#00d4b4', opacity: 0.26 },
  /**
   * ⚡ The primary button's own glow — `0 10px 28px rgba(255, 61, 113, 0.32)` in every
   * artboard, on the one button the screen is asking you to press. `glow` is teal and
   * belongs to positive state; reusing it here would put the wrong color under the
   * action. In `ADDED_BEYOND_BRIEF`.
   */
  accentGlow: { y: 10, blur: 28, spread: 0, color: '#ff3d71', opacity: 0.32 },
};

/**
 * ⚡ Gradients.
 *
 * The mockup leans on three and the brief has a slot for none of them, which
 * `MAPPING_TO_CONFIRM` already flags. They are **structured, not CSS strings** for the
 * same reason every other value here is: React Native cannot use `linear-gradient(…)`,
 * and each renderer formats from the angle and the stops.
 */
export interface Gradient {
  /** Degrees, CSS convention: 0 is up, 150 is down-and-right. */
  angle: number;
  stops: readonly { color: string; at?: number }[];
}

export const gradient: Record<'hero' | 'celebrate', Gradient> = {
  /** Behind a listing photo — and the whole surface when there is no photo yet. */
  hero: { angle: 150, stops: [{ color: '#2a2050' }, { color: '#101a30' }] },
  /** The won screen: a wash that fades into the page rather than a panel. */
  celebrate: { angle: 170, stops: [{ color: '#2b1338' }, { color: '#0b0f1a', at: 55 }] },
};

/**
 * ⚡ Motion.
 *
 * Easing is four numbers, not a CSS string: RN's `Easing.bezier` takes the same four,
 * and a `cubic-bezier(…)` literal would be exactly the CSS-only construct the brief
 * asks us to avoid.
 */
export const motion = {
  /**
   * `toast` is how long a success toast stays before it dismisses itself — the design
   * system's `--duration-toast`, 4 s. In `ADDED_BEYOND_BRIEF`.
   */
  duration: { fast: 120, base: 200, slow: 320, toast: 4000 },
  easing: {
    standard: [0.2, 0, 0, 1],
    entrance: [0, 0, 0, 1],
    exit: [0.3, 0, 1, 1],
  },
} as const satisfies { duration: Record<string, number>; easing: Record<string, readonly number[]> };

/** Layout widths that are design decisions rather than content. */
export const size = {
  /** How wide the readable column gets before it stops growing. */
  container: 1100,
  /** A feed card's minimum, which decides how many fit per row. */
  'card.min': 220,
  /**
   * ⚡ One column of content, at the width the artboards were drawn at. A bid screen is
   * a single decision — price, clock, one button — and stretching it across a laptop
   * puts the countdown a head-turn away from the button it belongs to.
   */
  phone: 430,
  /** The compose screen's photo. */
  thumbnail: 140,
  /**
   * ⚡ The lot's photo **inside the live bid panel** — a different job from the compose
   * screen's 140, which dwarfs a panel that has to share a phone with video, a clock and
   * two buttons. In `ADDED_BEYOND_BRIEF`.
   */
  'thumbnail.lot': 56,
  /**
   * The minimum tap target. Buyers bid one-handed, in motion, outdoors — the brief puts
   * this above density, so it is a token rather than a habit.
   */
  'target.min': 44,
  /** ⚡ The bar button in every artboard — 48px, 52px in a sheet. One step, the taller one. */
  'target.large': 52,
  /** Focus ring thickness and its gap from the control. */
  'focus.ring': 2,
  'focus.offset': 2,
  /**
   * ⚡ The live rail's card, from frame 1a of the app design.
   *
   * 118 wide by 150 tall is not a proportion — it is the width at which **three cards and
   * a sliver of a fourth** land on a 390pt frame, which is what tells a viewer the rail
   * scrolls. Change it and the rail stops advertising itself.
   */
  'card.live': 118,
  'card.live.media': 150,
  /** The shopper's avatar on frame 1q — the identity block, not a list thumbnail. */
  'avatar.profile': 62,
  /** ⚡ The category ring on frame 1a, and the column it sits in. */
  'chip.ring': 56,
  'chip.category': 66,
  /**
   * The admin console's frame, from Kelli's `Going Once Admin` design (the design
   * system's AppShell, 2026-10-08). A desk tool, not a phone: the nav rows sit below
   * `target.min`, which the brief set for thumbs. All in `ADDED_BEYOND_BRIEF`.
   */
  sidebar: 240,
  'bar.top': 64,
  'nav.item': 40,
  'nav.child': 36,
  /** The admin content column. `container` stays the harness pages' readable width. */
  'container.wide': 1440,
  /** A line icon in the admin nav and page chrome — the design system's `Icon size="md"`. */
  icon: 20,
  /** The admin filter side panel's width (the design system's Filters `aside`, 380). */
  'panel.side': 380,
  /** A person's initials in a table row — the design system's `Avatar` at its default 32. */
  'avatar.row': 32,
  // The admin Sellers page (Kelli's `Going Once Admin`, GO-613): the design system's
  // `DataTable`, `PageHeader`, `Tabs`, `OverflowMenu`, `Dialog`, `Toast` and `EmptyState`,
  // at the sizes the design system draws them. All in `ADDED_BEYOND_BRIEF`.
  /** The person on a record page's header — `Avatar size={56}`. */
  'avatar.header': 56,
  /** The gap and the accent ring around an avatar that is live — the design system's two 2px shadows. */
  'avatar.ring': 2,
  /** A card on a record page's overview grid: `minmax(min(100%, 380px), 1fr)`, where `card.min` is the feed's 220. */
  'card.detail': 380,
  /** The selected tab's underline — `--size-tab-indicator`. */
  'tab.indicator': 2,
  /** How far a SegmentedControl's thumb sits inside its track — `--size-segment-inset`. */
  'segment.inset': 2,
  /** A menu's width, between these two — `--size-menu-min` / `--size-menu-max`. */
  'menu.min': 200,
  'menu.max': 280,
  /** The visible ring of an icon-only button inside its `target.min` hit area — `MoreButton`'s 36. */
  'icon.button': 36,
  /** A dialog's width: `sm` for a short decision, `md` for a form — `--size-dialog-sm` / `--size-dialog-md`. */
  'dialog.sm': 400,
  'dialog.md': 560,
  /** A toast's widest — the design system's 358. */
  toast: 358,
  /** A data table's row and header heights — `--size-table-row` / `--size-table-header`. */
  'table.row': 56,
  'table.header': 40,
  /**
   * A table column's width, as a scale rather than per column: the design draws 64, 130,
   * 136, 148, 160, 200 and 220, which collapse to these four (`MAPPING_TO_CONFIRM`).
   */
  'column.actions': 64,
  'column.sm': 136,
  'column.md': 160,
  'column.lg': 220,
  /** A badge's height — the design system's `Badge` at 22. */
  badge: 22,
  /** The dot in a Live badge. */
  'live.dot': 5,
  /** The search box in a table's toolbar — `TableToolbar`'s 280. */
  search: 280,
  /** The disc behind an empty state's icon, and how wide its body runs — `EmptyState`'s 56 and 280. */
  'empty.icon': 56,
  'empty.body': 280,
} as const;

/**
 * **Slots we added beyond the brief, to feed back.**
 *
 * The brief invites this explicitly — "if a slot is missing or wrong, tell us and we'll
 * change the contract" — so rather than quietly inventing them, they are listed here and
 * should go back to Ciera's team with the next handoff:
 *
 * - `size.container` / `size.card.min` / `size.thumbnail` — layout widths that are
 *   design decisions, not content.
 * - `size.target.min` — the brief states the constraint in prose ("tap targets beat
 *   density"); a number makes it enforceable.
 * - `size.focus.ring` / `size.focus.offset` — accessibility is a launch requirement, and
 *   a focus ring nobody specified becomes a focus ring somebody removes.
 * - `family.base` / `family.mono` — the brief names a `type.mono` step but no family to
 *   render it in.
 * - `color.border.focus` — implied by the AA requirement, absent from the color list.
 */
/**
 * **Inferences in the extraction that Ciera's team should confirm or correct.**
 *
 * The mockup carries no token names, so every line below is engineering reading intent
 * out of a hex code. Each is a place the extraction could be wrong in a way that looks
 * right — which is exactly the kind of thing that gets discovered late.
 */
export const MAPPING_TO_CONFIRM = [
  'type.stat is 22px on frame 1i and 24px on the older timed-auction artboard — one of the two is the intended step',
  '#ff3d71 → accent.default AND live AND danger. It is the primary CTA, the current-bid amount, the LIVE badge and the logo. The design never shows a destructive action, so whether danger should be a different red is genuinely undecided.',
  '#00d4b4 → success AND bid.leading AND the secondary action. It is “good” and it is also “your max” and “See all” — arguably it wants its own `accent2` slot rather than borrowing `success`.',
  '#ffc247 → bid.outbid AND warning, and separately the gold referral gradient, which has no slot at all.',
  '#7b61ff / #a897ff (purple) has no slot. It carries the “Timed auction · not live” badge, toggles and surface gradients — meta rather than accent.',
  'fg.muted raised from the mockup’s rgba(255,255,255,.4) to .6, because .4 and .45 fail WCAG AA on bg.raised across 150 uses.',
  'Fifteen radii collapsed to four; twelve gap values collapsed to six.',
  'type.countdown is invented — the mockup has no distinct step for the timer.',
  'The mockup’s gradients (135deg #ff3d71→#7b61ff, #7b61ff→#00d4b4) and its 140deg surface gradients have no slots at all.',
  'Admin shell (Going Once Admin design, 2026-10-08) approximations: DS spacing 16→space.md (12), 24→space.lg (20), 2→space.xs (4), 21→space.lg (20) until the two space scales are reconciled; selected nav row neutral.tint + tint-border → bg.raised + border.strong; caps overline 11/800/0.12em → type.label (500, no tracking slot); nav label 14/600 → type.body (15/500), crumbs 12/600 → type.label; title’s 88% stretch dropped; “Admin” badge white-on-pink (3.4:1, fails AA) rendered as fg.inverse on accent.default; focus ring kept as border.focus (teal in dark, blue in light), not the DS white; placeholder border 1.5px dashed → 1px dashed border.strong; nav rows (40/36) below size.target.min — desk-only, confirm; light-theme teal (accent2) on white bg.bar is 1.90:1, so no teal text or icon is drawn until design picks a light accent2.',
  'Seller dashboard shell (Seller Dashboard Shell design, 2026-10-12) approximations: the top bar has no search field, no notification bell and no account menu — `TopBar` has none of the three, and all three are deferred rather than invented the week of the demo; the home nav item falls back to the literal “Home” because no seller is in context until impersonation settles, where the design makes the item the store’s own name (its own fallback word, but our reason for reaching it); the “Seller” badge and the selected nav row’s icon are accent2 where admin’s are accent, which is how staff tell the two consoles apart at a glance — drawn only because the surface is pinned dark (the light bar’s 1.90:1 teal is the entry above, still unresolved). NOT an approximation: the design’s Messages count is `unreadTotal || undefined` and hides itself at zero, so a blank shell drawing no count is the design’s own rule.',
  'Add Products (Smart Product Intake prototype, 2026-10-12) approximations: the design system’s indigo-text/indigo-tint (the Import tile) → meta/meta.subtle, which hold the same purple; accent-tint (the Voice add tile) → accent.subtle; fg-subtle (.45) and fg-faint (.4) → fg.muted, because both fail WCAG AA on bg.raised, the same rungs fg.muted was raised from; the Smart Add hero’s photo and confidence chips are an illustration in tokens rather than a product photo; teal button ink is fg.inverse.',
  'Admin audit log (Seller Dashboard Shell S08, 2026-10-10) approximations: the row avatar’s pink→purple gradient-hero with on-brand initials → meta on meta.subtle (the brand gradient has no slot, and white on its pink end is the 3.4:1 the Admin badge already avoids); table header caps 800/0.06em → type.label (no tracking slot); the filter chips’ accent2 tint when active → accent.subtle + accent.default, for the light-theme teal reason above; light bg.sheet is #ffffff, the same as bg.bar and bg.base, so the filter panel is set apart only by its border and shadow until design gives light a sheet.',
  'Admin Sellers page primitives (Going Once Admin design and its design system, GO-613) approximations: Badge `leading` → success on success.subtle where the design system draws accent2 ink on accent2.tint (the light-theme teal is 1.90:1), `timed` → meta on meta.subtle in both themes (the design system fills it in light), `quiet` → fg.muted on neutral.tint, `live` → fg.inverse on live (the design system draws white), radius.xs → radius.sm, badge caps 800 with tracking → type.label at type.title.weight, and the Live dot does not pulse (no keyframes slot); the avatar with no photo is initials in meta on meta.subtle, not on-brand ink on gradient.hero (as the audit row already was), and its initials are type.label in a row and type.stat in a header rather than 38% of the size; a table row’s hover is neutral.tint (no row.hover slot) and the open row bg.raised, where the design system layers neutral.tint for both; table text 13 → type.body, header caps 0.06em → type.label, and the header rule stays border.default (the design system draws border.strong) with the table’s existing space.md cell padding, under the 56/40 row and header heights, which are floors; a menu row is size.target.min (44) where the design system uses 36 with a mouse, and its 600 label → type.body; the dialog is radius.lg where the design system uses radius.xl, and its 24/800 title → type.title; the toast is radius.lg with no tone but success; an empty state’s 24 icon → size.icon (20) and its 15/800 title → type.title; column widths 130/136/148/160/200 collapse to column.sm (136), column.md (160) and column.lg (220); a narrow page header does not fold its secondary actions into ••• (the design system does under 800).',
] as const;

export const ADDED_BEYOND_BRIEF = [
  'size.container',
  'size.phone (the single-column width the artboards are drawn at)',
  'type.hero (the won screen’s lockup — one step above display, and the only place it is used)',
  'size.card.min',
  'size.thumbnail',
  'size.target.min',
  'size.focus.ring',
  'size.focus.offset',
  'size.card.live + size.card.live.media (the live rail card on frame 1a — the width is a scroll affordance, not a proportion)',
  'size.chip.ring + size.chip.category (the category rings on frame 1a)',
  'size.avatar.profile (the shopper avatar on frame 1q)',
  'color.accent2.default + color.bg.bar (from the design system in the nav review — the seller mode\u2019s accent, and the bar surface that is not bg.base)',
  'type.title (an item name is upright where heading is italic — frame 1i sets it 800 17px/1.2)',
  'type.stat (the number on a stat card, between heading and countdown — frame 1i sets it 800 22px/1)',
  'family.base',
  'family.mono',
  'color.border.focus',
  'elevation.glow',
  'type.*.style (the italic axis the display steps need)',
  'color.meta + color.meta.subtle (the purple carrying "timed auction · not live", toggles and surface gradients — meta, not accent: it says what kind of thing this is, not what to do about it)',
  'elevation.accentGlow (the pink lift under every primary CTA in the artboards; `glow` is teal and belongs to positive state)',
  'gradient.hero + gradient.celebrate (two surface gradients the mockup leans on and the brief has no slot for, structured rather than CSS strings so RN can render them)',
  'size.thumbnail.lot (the lot photo in the live bid panel — `size.thumbnail` is the compose screen’s and is four times too large here)',
  'color.overlay.fg.muted (the label rank on the live bid panel — the brief has one overlay foreground, and a panel over moving video needs two: the numbers and the words naming them)',
  'size.sidebar + size.bar.top (the admin console’s side nav width and top bar height — the design system’s AppShell)',
  'size.nav.item + size.nav.child (admin nav row heights, 40 and 36 — below size.target.min on purpose: a desk tool)',
  'size.container.wide (the admin content column, max 1440; size.container stays the harness pages’ 1100)',
  'size.icon (a line icon in the admin chrome, 20 — the design system’s Icon size md)',
  'color.bg.sheet (menus and the admin filter side panel — the design system’s bg.sheet, #111A2E)',
  'size.panel.side (the admin filter side panel, 380 wide — the design system’s Filters aside)',
  'size.avatar.row (initials in an admin table row, 32 — the design system’s Avatar default)',
  'color.accent2.hover + color.accent2.tint + color.accent2.tint.border + color.neutral.tint + color.neutral.tint.border (the design system’s teal button and tint slots, for Add Products — D61)',
  'size.avatar.header + size.avatar.ring (the 56 avatar on a record page’s header, and the 2 + 2 ring a live one wears — the design system’s Avatar)',
  'size.card.detail (a record page’s overview card, 380 at its narrowest — size.card.min is the feed’s 220)',
  'size.tab.indicator (the selected tab’s 2px underline — the design system’s Tabs)',
  'type.caps (the 800 11px capitals of navigation labels — the design system’s Tabs and SegmentedControl; its 0.06em tracking has no slot and is not drawn) + size.segment.inset (the 2px between a SegmentedControl’s track and thumb)',
  'size.menu.min + size.menu.max (a menu runs 200–280 wide — the design system’s OverflowMenu)',
  'size.icon.button (the 36 ring of the ••• button inside its 44 hit area — the design system’s MoreButton)',
  'size.dialog.sm + size.dialog.md (400 for a short decision, 560 for a form — the design system’s Dialog)',
  'size.toast (a toast’s widest, 358 — the design system’s Toast)',
  'size.table.row + size.table.header (56 and 40 — the design system’s DataTable)',
  'size.column.actions + size.column.sm + size.column.md + size.column.lg (table column widths as a four-step scale — the design draws seven)',
  'size.badge + size.live.dot (a badge’s 22 height and the Live badge’s dot — the design system’s Badge)',
  'size.search (the search box in a table toolbar, 280 — the design system’s TableToolbar)',
  'size.empty.icon + size.empty.body (an empty state’s 56 icon disc and its 280 body — the design system’s EmptyState)',
  'motion.duration.toast (how long a success toast stays, 4 s — the design system’s --duration-toast)',
] as const;

// ── Names and rendering ──────────────────────────────────────────────────────

export type ColorName = `color.${keyof Palette}`;
export type SpaceName = `space.${keyof typeof space}`;
export type RadiusName = `radius.${keyof typeof radius}`;
export type TypeName =
  `type.${keyof typeof type}.${'size' | 'line' | 'weight' | 'style'}` | `family.${keyof typeof family}`;
export type ElevationName = `elevation.${keyof typeof elevation}`;
export type GradientName = `gradient.${keyof typeof gradient}`;
export type MotionName =
  `motion.duration.${keyof (typeof motion)['duration']}` | `motion.easing.${keyof (typeof motion)['easing']}`;
export type SizeName = `size.${keyof typeof size}`;

export type TokenName =
  ColorName | SpaceName | RadiusName | TypeName | ElevationName | GradientName | MotionName | SizeName;

/** `color.bg.base` → `--go-color-bg-base`. */
export function cssVariableName(name: TokenName): string {
  return `--go-${name.replace(/\./g, '-')}`;
}

/** `var(--go-space-md)` — what every component writes instead of a value. */
export function cssVar(name: TokenName): string {
  return `var(${cssVariableName(name)})`;
}

/** A gradient as CSS. RN's renderer reads the same structure and hands it to its own. */
function linearGradient(value: Gradient): string {
  const stops = value.stops.map((stop) => (stop.at === undefined ? stop.color : `${stop.color} ${stop.at}%`));
  return `linear-gradient(${value.angle}deg, ${stops.join(', ')})`;
}

function shadow(value: Elevation): string {
  const { y, blur, spread, color, opacity } = value;
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `0 ${y}px ${blur}px ${spread}px ${color}${alpha}`;
}

/** Everything that does not change with the theme. */
function structuralCss(): string[] {
  const lines: string[] = [];
  for (const [step, value] of Object.entries(space)) lines.push(`  --go-space-${step}: ${value}px;`);
  for (const [step, value] of Object.entries(radius)) lines.push(`  --go-radius-${step}: ${value}px;`);
  // Every dot, as `cssVariableName` does: a three-part slot (`card.live.media`) otherwise
  // emits a variable no `cssVar` call names.
  for (const [step, value] of Object.entries(size)) lines.push(`  --go-size-${step.replace(/\./g, '-')}: ${value}px;`);
  for (const [step, value] of Object.entries(type)) {
    lines.push(`  --go-type-${step}-size: ${value.size}px;`);
    // Unitless: a line height that scales with the size rather than fighting it.
    lines.push(`  --go-type-${step}-line: ${value.line};`);
    lines.push(`  --go-type-${step}-weight: ${value.weight};`);
    lines.push(`  --go-type-${step}-style: ${value.italic ? 'italic' : 'normal'};`);
  }
  for (const [step, value] of Object.entries(family)) lines.push(`  --go-family-${step}: ${value};`);
  for (const [step, value] of Object.entries(elevation)) lines.push(`  --go-elevation-${step}: ${shadow(value)};`);
  for (const [step, value] of Object.entries(gradient))
    lines.push(`  --go-gradient-${step}: ${linearGradient(value)};`);
  for (const [step, value] of Object.entries(motion.duration))
    lines.push(`  --go-motion-duration-${step}: ${value}ms;`);
  for (const [step, value] of Object.entries(motion.easing)) {
    lines.push(`  --go-motion-easing-${step}: cubic-bezier(${value.join(', ')});`);
  }
  return lines;
}

function paletteCss(palette: Palette): string[] {
  return Object.entries(palette).map(([slot, value]) => `  --go-color-${slot.replace(/\./g, '-')}: ${value};`);
}

/**
 * The whole contract as CSS custom properties.
 *
 * Dark on `:root` because it is the default; light under an explicit attribute, so a
 * theme is a data attribute swap rather than a re-render.
 *
 * **`[data-theme='dark']` is emitted beside it, and the asymmetry it fixes is real**
 * (D46 addendum, 2026-10-12). Without that selector the attribute could only ever say
 * "light here": once `html` carried `data-theme="light"` — which `apps/web/src/main.tsx`
 * sets on a light-mode OS — **no descendant could get back to dark**, so a surface the
 * design specifies as dark-only rendered light, with teal on the white bar at 1.90:1. The
 * design system's own `colors.css` writes `:root,[data-theme="dark"]` for this reason.
 * Nothing about the default changes: `:root` still carries dark.
 */
export function tokensToCss(): string {
  // **`color-scheme` says which theme the browser should draw its own controls for**: the date picker's
  // calendar icon, scrollbars, a select's popup, a checkbox's box. Left unsaid, a dark page gets the
  // light-page versions: a dark calendar icon on a dark background, which cannot be seen. It moves with
  // `data-theme`, so a subtree pinned dark inside a light page draws dark controls too.
  return [
    `:root {\n${['  color-scheme: dark;', ...structuralCss(), ...paletteCss(themes.dark)].join('\n')}\n}`,
    `[data-theme='dark'] {\n${['  color-scheme: dark;', ...paletteCss(themes.dark)].join('\n')}\n}`,
    `[data-theme='light'] {\n${['  color-scheme: light;', ...paletteCss(themes.light)].join('\n')}\n}`,
  ].join('\n\n');
}

/** Every token name — for the enforcement tests and for whoever fills in the values. */
export function tokenNames(): TokenName[] {
  return [
    ...Object.keys(themes.dark).map((slot) => `color.${slot}` as TokenName),
    ...Object.keys(space).map((step) => `space.${step}` as TokenName),
    ...Object.keys(radius).map((step) => `radius.${step}` as TokenName),
    ...Object.keys(size).map((step) => `size.${step}` as TokenName),
    ...Object.entries(type).flatMap(([step]) =>
      (['size', 'line', 'weight', 'style'] as const).map((part) => `type.${step}.${part}` as TokenName),
    ),
    ...Object.keys(family).map((step) => `family.${step}` as TokenName),
    ...Object.keys(elevation).map((step) => `elevation.${step}` as TokenName),
    ...Object.keys(gradient).map((step) => `gradient.${step}` as TokenName),
    ...Object.keys(motion.duration).map((step) => `motion.duration.${step}` as TokenName),
    ...Object.keys(motion.easing).map((step) => `motion.easing.${step}` as TokenName),
  ];
}
